type LayerResult = {
  id?: string;
  name?: string;
  kind?: string;
  totalCount?: number | null;
  stats?: Record<string, number | null>;
  groupCounts?: Record<string, Array<{ value?: string; count?: number }>>;
  dailyCounts?: Array<{ day?: string; count?: number }>;
  exemplars?: Array<Record<string, unknown>>;
  notes?: string[];
  queryErrors?: Array<{ query?: string; error?: string }>;
};

type LayerPayload = {
  LayerQueryResults?: {
    window?: { label?: string | null };
    layers?: LayerResult[];
  } | null;
  notes?: string[];
  error?: string;
};

const STAT_LABELS: Record<string, string> = {
  max_wind: 'Highest wind',
  max_gust: 'Highest gust',
  max_surge: 'Highest storm surge',
  storm_count: 'Storms',
  acres_sum: 'Acres',
  population_exposed_sum: 'People exposed',
};

const GROUP_LABELS: Record<string, string> = {
  STORMTYPE: 'Storm type',
  STORMNAME: 'Storm name',
  FCSTPRD: 'Forecast period',
  TCDVLP: 'Development',
  FireCause: 'Fire cause',
  fema_classification: 'Damage',
};

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function humanKey(key: string, labels: Record<string, string>): string {
  if (labels[key]) return labels[key];
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function exemplarName(row: Record<string, unknown>): string | null {
  const value =
    row.IncidentName ||
    row.STORMNAME ||
    row.COUNTY_NAME ||
    row.county ||
    row.name ||
    row.Name ||
    row.label ||
    row.Event;
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function formatLayerBlock(layer: LayerResult): string {
  const title = layer.name?.trim() || 'Layer';
  const parts: string[] = [`<h4>${escapeHtml(title)}</h4>`];
  if (typeof layer.totalCount === 'number') {
    const noun = layer.totalCount === 1 ? 'feature' : 'features';
    parts.push(
      `<p>${formatCount(layer.totalCount)} ${noun} in this layer.</p>`
    );
  }
  const stats = Object.entries(layer.stats || {}).filter(
    ([, value]) => typeof value === 'number' && Number.isFinite(value)
  );
  if (stats.length > 0) {
    const sentence = stats
      .map(
        ([key, value]) =>
          `${humanKey(key, STAT_LABELS)}: ${formatCount(value as number)}`
      )
      .join('. ');
    parts.push(`<p>${escapeHtml(sentence)}.</p>`);
  }
  for (const [key, rows] of Object.entries(layer.groupCounts || {})) {
    if (!rows?.length) continue;
    const items = rows
      .slice(0, 15)
      .map(
        (row) => `${row.value ?? 'Unknown'} (${formatCount(row.count ?? 0)})`
      )
      .join(', ');
    parts.push(
      `<p>${escapeHtml(`${humanKey(key, GROUP_LABELS)}: ${items}.`)}</p>`
    );
  }
  if (layer.dailyCounts && layer.dailyCounts.length > 0) {
    const items = layer.dailyCounts
      .slice(0, 15)
      .map(
        (day) =>
          `<li>${escapeHtml(day.day)}: ${escapeHtml(formatCount(day.count ?? 0))}</li>`
      )
      .join('');
    parts.push(`<p>Daily counts</p><ul>${items}</ul>`);
  }
  const names = (layer.exemplars || [])
    .map(exemplarName)
    .filter((name): name is string => !!name)
    .slice(0, 12);
  if (names.length > 0) {
    parts.push(`<p>Examples: ${escapeHtml(names.join(', '))}.</p>`);
  }
  if (layer.queryErrors && layer.queryErrors.length > 0) {
    parts.push('<p>Some statistics for this layer could not be loaded.</p>');
  }
  const notes = (layer.notes || []).map((note) => note.trim()).filter(Boolean);
  if (notes.length > 0) {
    parts.push(
      `<ul>${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>`
    );
  }
  return parts.join('');
}

function formatLayerPayload(payload: LayerPayload): string | null {
  if (payload.error) {
    return `<p>Analytics could not be loaded. ${escapeHtml(payload.error)}</p>`;
  }
  const results = payload.LayerQueryResults;
  if (!results?.layers?.length) return null;
  const parts: string[] = [];
  if (results.window?.label) {
    parts.push(`<p>Time window: ${escapeHtml(results.window.label)}.</p>`);
  }
  parts.push(...results.layers.map(formatLayerBlock));
  const notes = (payload.notes || [])
    .map((note) => note.trim())
    .filter(Boolean);
  if (notes.length > 0) {
    parts.push(
      `<ul>${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>`
    );
  }
  return parts.join('');
}

function formatTasks(parsed: Record<string, unknown>): string | null {
  const actions = Array.isArray(parsed.actions) ? parsed.actions : null;
  if (!actions) return null;
  const items = actions
    .map((action) => {
      if (!action || typeof action !== 'object') return null;
      const row = action as Record<string, unknown>;
      const name = typeof row.name === 'string' ? row.name.trim() : '';
      if (!name) return null;
      const status = typeof row.status === 'string' ? row.status : '';
      return `<li>${escapeHtml(name)}${status ? ` (${escapeHtml(status)})` : ''}</li>`;
    })
    .filter((item): item is string => !!item);
  if (items.length === 0) return null;
  return `<ul>${items.join('')}</ul>`;
}

function readableFromDataString(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    if ('LayerQueryResults' in parsed || parsed.error || parsed.notes) {
      const html = formatLayerPayload(parsed as LayerPayload);
      if (html) return html;
    }
    const tasks = formatTasks(parsed);
    if (tasks) return tasks;
    return null;
  } catch {
    return null;
  }
}

function layerHtmlFromDataString(
  raw: string | null | undefined
): string | null {
  if (!raw || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!('LayerQueryResults' in parsed) && !parsed.error && !parsed.notes) {
      return null;
    }
    return formatLayerPayload(parsed as LayerPayload);
  } catch {
    return null;
  }
}

/**
 * Export body for one section. Analytics becomes sentences and lists. Other
 * sections keep their saved HTML so paragraphs and tables survive. An empty
 * section falls back to a task list stored on the version, then a short note.
 */
export function readableSectionHtml(section: {
  sitrep_section_text?: string | null;
  data_string?: string | null;
}): string {
  const layers = layerHtmlFromDataString(section.data_string);
  if (layers) return layers;
  const text = section.sitrep_section_text?.trim() || '';
  if (text) return text;
  const tasks = readableFromDataString(section.data_string);
  if (tasks) return tasks;
  return '<p>No content in this section.</p>';
}
